<template>
  <div class="chatter-drawer-header">
    <div class="chatter-drawer-header__avatar" :class="{ 'chatter-drawer-header__avatar--object': !isEmployeeChatter }">
      <ExAvatar
        v-if="isEmployeeChatter"
        size="large"
        :picture="avatar.picture || null"
        :firstName="avatar.firstName || ''"
        :lastName="avatar.lastName || ''"
      />
      <Icon v-else :name="iconName || 'org_icon_role'" />
    </div>

    <div class="chatter-drawer-header__titles">
      <div class="chatter-drawer-header__type">{{ type }}</div>
      <div class="chatter-drawer-header__name">{{ typeValue }}</div>
    </div>

    <slot name="follow">
      <div
        v-if="followVisible"
        ref="followWrap"
        class="chatter-drawer-header__follow-wrap"
        @keydown="onFollowKeydown"
      >
        <button
          ref="followButton"
          type="button"
          class="chatter-drawer-header__follow"
          :class="{ 'chatter-drawer-header__follow--following': following }"
          :aria-haspopup="following ? 'menu' : null"
          :aria-expanded="following ? String(menuOpen) : null"
          :aria-busy="followLoading ? 'true' : 'false'"
          data-testid="chatter-drawer-follow"
          @click="onFollowClick"
        >
          <v-progress-circular
            v-if="followLoading"
            indeterminate
            :size="16"
            :width="2"
            class="chatter-drawer-header__follow-loader"
          />
          <v-icon v-else size="20">{{ following ? "mdi-check-circle-outline" : "mdi-bell-outline" }}</v-icon>
          <span class="chatter-drawer-header__follow-label">{{ following ? "Following" : "Follow" }}</span>
          <v-icon v-if="following" size="20">mdi-chevron-down</v-icon>
        </button>

        <FollowMenu
          v-if="following && menuOpen"
          class="chatter-drawer-header__follow-menu"
          :cadence="cadence"
          :name="typeValue"
          autofocus
          @cadence-change="$emit('cadence-change', $event)"
          @unfollow="onUnfollow"
          @close="closeMenu(true)"
        />
      </div>
    </slot>

    <button
      type="button"
      class="chatter-drawer-header__close"
      aria-label="Close"
      data-testid="chatter-drawer-close"
      @click="$emit('close')"
    >
      <v-icon>mdi-close</v-icon>
    </button>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";
import FollowMenu from "@components/FollowMenu/FollowMenu.vue";
import Icon from "@components/icons/Icon.vue";

const props = defineProps({
  type: {
    type: String,
    default: "",
  },
  typeValue: {
    type: String,
    default: "",
  },
  iconName: {
    type: String,
    default: "org_icon_role",
  },
  avatar: {
    type: Object,
    default: () => ({}),
  },
  isEmployeeChatter: {
    type: Boolean,
    default: false,
  },
  following: {
    type: Boolean,
    default: false,
  },
  /** Render the default Follow/Following control when no follow slot is provided. Off unless the object is followable. */
  followVisible: {
    type: Boolean,
    default: false,
  },
  /** A follow or unfollow request is in flight. */
  followLoading: {
    type: Boolean,
    default: false,
  },
  /** Saved notification cadence shown in the Following menu. */
  cadence: {
    type: String,
    default: "daily",
  },
});

const emit = defineEmits(["close", "follow-toggle", "cadence-change"]);

const followWrap = ref(null);
const followButton = ref(null);
const menuOpen = ref(false);

function onFollowClick() {
  if (props.followLoading) return;
  if (props.following) {
    menuOpen.value = !menuOpen.value;
    return;
  }
  emit("follow-toggle", true);
}

function closeMenu(returnFocus = false) {
  menuOpen.value = false;
  if (returnFocus) nextTick(() => followButton.value?.focus());
}

function onUnfollow() {
  closeMenu(true);
  emit("follow-toggle", false);
}

function onFollowKeydown(event) {
  if (event.key !== "Escape" || !menuOpen.value) return;
  event.stopPropagation();
  closeMenu(true);
}

function onDocumentPointerDown(event) {
  if (followWrap.value && !followWrap.value.contains(event.target)) closeMenu();
}

watch(menuOpen, (open) => {
  if (open) document.addEventListener("mousedown", onDocumentPointerDown);
  else document.removeEventListener("mousedown", onDocumentPointerDown);
});

watch(
  () => props.following,
  (value) => {
    if (!value) closeMenu();
  }
);

onBeforeUnmount(() => document.removeEventListener("mousedown", onDocumentPointerDown));
</script>

<style scoped>
.chatter-drawer-header {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 600px;
  height: 72px;
  padding: 20px 24px 12px 24px;
  background: #ffffff;
  box-sizing: border-box;
}

.chatter-drawer-header__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
}

.chatter-drawer-header__avatar--object {
  border: 1px solid #eaecf0;
  box-shadow: 0px 1px 2px 0px #1018280d;
}

.chatter-drawer-header__titles {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.chatter-drawer-header__type {
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
  text-transform: capitalize;
}

.chatter-drawer-header__name {
  font-family: Inter;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #101828;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chatter-drawer-header__follow-wrap {
  position: relative;
  flex-shrink: 0;
}

.chatter-drawer-header__follow-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 20;
}

.chatter-drawer-header__follow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
  white-space: nowrap;
}

.chatter-drawer-header__follow--following {
  background: #f2edff;
  border-color: #f2edff;
  color: #6941c6;
}

.chatter-drawer-header__follow[aria-busy="true"] {
  cursor: progress;
}

.chatter-drawer-header__follow .v-icon {
  color: inherit;
}

.chatter-drawer-header__follow:focus-visible,
.chatter-drawer-header__close:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.chatter-drawer-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  /* Design uses #98a2b3, but that is 2.58:1 on white; #667085 keeps 4.97:1 (WCAG AA). */
  color: #667085;
  cursor: pointer;
}
</style>
